/**
 * Le chiavi che la TMA sa leggere dentro `match.externalData`, PER PROVIDER.
 *
 * `externalData` e' opaco per contratto (qTournaments 1.68.0, ADR-0157 `proposed`, kb#203): il
 * motore stocca la risposta intera del provider all'apertura sotto `opened` e il referto sotto
 * `reported`, senza schema per gioco. ⇒ Il client legge SOLO le chiavi che conosce per quel
 * provider e ignora le altre. Una chiave qui e' una misura sul provider, non una convenzione.
 *
 * ⚠️ Oggi sul telefono arriva sempre `null`: il bordo lo fa passare con `qlash-limulo#127`.
 */

import { LIMULO_RESULT_MODE_MANUAL, LIMULO_RESULT_MODE_PROVIDER } from './match.ts';

/** L'invito che il provider Clash Royale ha creato per questo incontro. */
export interface LimuloCrInvite {
  /** Il tag dell'invito, com'e' (es. `#2CQR0YL`). Mai vuoto. */
  tag: string;
  /** `remoteMatchState` del provider (`created`, …), o `null` se non l'ha detto. */
  state: string | null;
  /**
   * `true` quando l'invito e' MORTO. Due fonti, e l'ordine conta (kb#268, 2026-09-24):
   *  1. **`externalMatch.status` ∈ `conflict|failed|cancelled`** — la fonte PRINCIPALE. Lo scrive
   *     qTournaments 1.70.0 quando il provider riporta la partita andata storta
   *     (`external-status.update`), e il bordo lo porta gia' (`sanitizePublicMatch`,
   *     `externalMatch.status`). E' lo stato dell'INCONTRO, riscritto quando cambia.
   *  2. `remoteMatchState: 'timedOut'` dentro `externalData.opened` — SECONDARIA, e quasi muta:
   *     `opened` e' la risposta del provider ALL'APERTURA, e il motore non la riscrive mai. Resta
   *     perche' non costa niente e non puo' affermare il falso: se c'e', l'invito e' morto davvero.
   * Tutto il resto (compreso `null`) NON e' una prova di scadenza.
   */
  expired: boolean;
}

/**
 * Gli stati di `externalMatch.status` che dicono «la partita nel gioco e' morta» (qTournaments
 * 1.70.0, kb#264/kb#268): nessuno dei tre porta un esito automatico. Chi ne decide il seguito NON e'
 * lo stesso (`partitaMortaEsitoOf`, kb#304): un conflitto si arbitra (lo staff), un fallimento o un
 * annullamento li RILANCIA il torneo da solo.
 * ⚠️ Confronto ESATTO: il motore scrive questi tre valori e nessun altro li significa.
 */
export const LIMULO_EXTERNAL_MATCH_DEAD = ['conflict', 'failed', 'cancelled'] as const;

/**
 * La partita presso il provider e' morta? Legge SOLO `externalMatch.status`.
 *
 * ⚠️ **Vivo sul servito di oggi**, a differenza di `matchCheckIn`: la 1.70.0 e' in esercizio su
 * staging (`qlash-limulo#136`, merge `802d6ee`) e il sanitizer porta `status` da sempre.
 * ⛔ `externalStatusReason` (la frase del provider) NON attraversa il bordo — `sanitizePublicMatch`
 * e' un letterale a sei chiavi su `externalMatch` (`policy.js:568-580` @ `origin/staging`
 * `802d6ee`) — e qui non si cerca: una frase del provider stampata grezza non sarebbe comunque
 * copy nostra.
 */
export function externalMatchDeadOf(externalMatch: { status: string } | null | undefined): boolean {
  if (externalMatch === null || externalMatch === undefined) return false;
  return (LIMULO_EXTERNAL_MATCH_DEAD as readonly string[]).includes(externalMatch.status);
}

/**
 * Il vocabolario CHIUSO di `externalMatch.status`: gli otto stati della lista bianca del motore,
 * `STATI_ESTERNI` [M `qTournaments@origin/main` `f874bdc`, `src/app/core/partite-esterne.ts:130-139`;
 * gli stessi otto, nello stesso ordine, nel servito: `qlash-limulo@origin/staging` `802d6ee`,
 * `packages/app-qtournaments/app/core/partite-esterne.js:115-124`, qTournaments 1.70.0].
 *
 * Perche' serve (tma-orc 38, 2026-09-24): la sezione «Partita esterna» della scheda stampava lo
 * stato GREZZO — « · conflict» — cioe' vocabolario del motore al posto di una frase. Con la 1.70.0
 * lo stato arriva davvero (prima il bordo lo portava sempre vuoto), quindi la parola grezza sarebbe
 * finita sul telefono proprio nel caso peggiore: la partita nel gioco morta.
 *
 * ⚠️ **Otto e non nove.** Il provider (`app-games/models.js`) conosce anche `not_required`, e il
 * motore lo esclude apposta dalla sua lista (e' una risposta alla creazione, non una fase della
 * vita della partita): qui vale la lista del motore, e `not_required` cade in `unknown`.
 * ⚠️ **Oggi il motore ne SCRIVE solo tre** (`conflict|failed|cancelled`: `externalStatus` in
 * `persistenza/match.model.js:1400`, e il filtro in `superficie/capability/incontri.js:2003`):
 * gli altri cinque non arrivano dal servito, ma sono stati che la lista ammette, e una mappa che
 * li tacesse li farebbe leggere «sconosciuto» il giorno che cominciano ad arrivare.
 */
export const LIMULO_EXTERNAL_MATCH_STATUSES = [
  'pending',
  'awaiting_players',
  'awaiting_result',
  'running',
  'completed',
  'conflict',
  'failed',
  'cancelled',
] as const;

/** La chiave di copy (`matches.detail.externalStatus.<chiave>`) per uno stato esterno. */
export type LimuloExternalMatchStatusLabel =
  | (typeof LIMULO_EXTERNAL_MATCH_STATUSES)[number]
  | 'unknown';

/**
 * Quale frase dire per `externalMatch.status` — e MAI la stringa grezza.
 *
 * Confronto ESATTO, come in `externalMatchDeadOf`: un valore fuori lista (compreso `''`, cioe'
 * «il provider non ha detto niente», che e' come il bordo normalizza l'assenza: `status || ''` in
 * `sanitizePublicMatch`) da' `unknown`, che a schermo e' «stato del gioco sconosciuto». Tradurre un
 * valore ignoto vorrebbe dire inventare una corrispondenza che nessuno ha promesso; stamparlo
 * grezzo vorrebbe dire far leggere al giocatore il vocabolario del motore.
 */
export function externalMatchStatusLabelOf(
  status: string | null | undefined,
): LimuloExternalMatchStatusLabel {
  const s = typeof status === 'string' ? status : '';
  return (LIMULO_EXTERNAL_MATCH_STATUSES as readonly string[]).includes(s)
    ? (s as LimuloExternalMatchStatusLabel)
    : 'unknown';
}

/**
 * Chi decide, su una partita nel gioco morta (kb#304, qTournaments 1.89.0 — `incontri-fermi.js:16-23`
 * @ `qlash-limulo` `origin/staging`):
 *  · `staff`    — `conflict`: e' un arbitrato, la decisione e' dello staff;
 *  · `rilancio` — `failed`/`cancelled` con `resultMode: 'provider'`: il torneo rilancia da solo la
 *                 partita, al piu' `RILANCI_AUTOMATICI` volte;
 *  · `referto`  — `failed`/`cancelled` con `resultMode: 'manual'`: i rilanci sono esauriti e il
 *                 risultato lo segnalano i giocatori;
 *  · `ignoto`   — `failed`/`cancelled` col modo vuoto o fuori lista (bordo vecchio): non si
 *                 promette ne' il rilancio ne' il referto, si dice solo che la partita e' morta.
 * ⚠️ Nessun conteggio dei rilanci e nessun orario: la facciata non li espone.
 * 📌 **Scritto sul motore di OGGI (qTournaments 1.89+, 2 rilanci automatici).** Da rivedere quando
 * kb#340 (I-329, «chi si arrende perde; due rese = annullata e NESSUN nuovo invito») cambia il motore:
 * un `cancelled` da doppia resa NON si rilancera' piu', e la scelta `cancelled` + `provider` ⇒ `rilancio`
 * di `partitaMortaEsitoOf` diventera' falsa per quel caso.
 */
export type LimuloPartitaMortaEsito = 'staff' | 'rilancio' | 'referto' | 'ignoto';

/**
 * L'esito di una partita morta, o `null` se la partita non e' morta (`externalMatchDeadOf`).
 * Confronto ESATTO su `status` e su `resultMode`, come il resto: un valore fuori lista e' `ignoto`,
 * mai un rilancio promesso a vuoto.
 */
export function partitaMortaEsitoOf(
  externalMatch: { status: string } | null | undefined,
  resultMode: string | null | undefined,
): LimuloPartitaMortaEsito | null {
  if (!externalMatchDeadOf(externalMatch)) return null;
  if (externalMatch?.status === 'conflict') return 'staff';
  if (resultMode === LIMULO_RESULT_MODE_PROVIDER) return 'rilancio';
  if (resultMode === LIMULO_RESULT_MODE_MANUAL) return 'referto';
  return 'ignoto';
}

/**
 * La chiave di copy di `matches.detail.<oggetto>.<stato>` quando la frase di `externalStatus` NON basta:
 * su `failed`/`cancelled` dice chi decide (`partitaMortaEsitoOf`) — `externalStatusRetry` se il torneo
 * rilancia, `externalStatusManual` se il risultato lo segnalano i giocatori. `null` negli altri casi
 * (`conflict`, stati vivi, modo non dichiarato): resta `externalStatus.<chiave>` di `externalMatchStatusLabelOf`,
 * che su `failed`/`cancelled` e' la frase neutra.
 */
export function externalMatchStatusEsitoKeyOf(
  externalMatch: { status: string } | null | undefined,
  resultMode: string | null | undefined,
): string | null {
  const stato = externalMatch?.status;
  if (stato !== 'failed' && stato !== 'cancelled') return null;
  const esito = partitaMortaEsitoOf(externalMatch, resultMode);
  return esito === 'rilancio'
    ? `externalStatusRetry.${stato}`
    : esito === 'referto'
      ? `externalStatusManual.${stato}`
      : null;
}

/** La chiave di copy di `actions.openGame.<chiave>`: la frase della partita morta. */
export function partitaMortaFraseKeyOf(esito: LimuloPartitaMortaEsito): string {
  return esito === 'staff'
    ? 'gameDeadConflict'
    : esito === 'rilancio'
      ? 'gameDead'
      : esito === 'referto'
        ? 'gameDeadManual'
        : 'gameDeadUnknown';
}

const asRecord = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/** Il `profileType` di Tennis Clash su Limulo (`app-game-tennis-clash`, `GET /v1/games` → `profileType`). */
export const LIMULO_TENNIS_CLASH_PROFILE_TYPE = 'tennis-clash';

/**
 * La partita che il provider Tennis Clash ha creato per un incontro, come la TMA la sa leggere.
 * Ogni campo e' `null` quando il provider non l'ha detto (o l'ha detto in una forma che non si apre).
 */
export interface LimuloTennisClashPartita {
  /** Il link per Android, com'e' arrivato. `null` = assente o di uno schema che non apriamo. */
  deepLinkAndroid: string | null;
  /** Il link per iOS, com'e' arrivato. `null` = assente o di uno schema che non apriamo. */
  deepLinkIos: string | null;
  /** Il codice della partita, se il provider l'ha dato. Mai vuoto. */
  matchCode: string | null;
}

/**
 * Gli schemi che un link di Tennis Clash puo' avere: `https` (Android: `https://tennis-clash.wls.studio/
 * partyMatch?…`) e `tennisclash` (iOS: `tennisclash://partyMatch?…`) [M `qlash-limulo@origin/staging`
 * `cf89580`, `packages/app-game-tennis-clash/backend/provider.js:137-138`]. ⚠️ Una lista CHIUSA e non
 * un «qualunque schema»: il valore arriva dal payload del provider e va a `openLink`, quindi un
 * `javascript:` o un `data:` non devono poterci passare. Se il provider cambiasse schema il link
 * sparisce e la scheda dice che non c'e' — un guasto visibile, non un link sbagliato.
 */
const SCHEMI_LINK_TENNIS_CLASH = /^(?:https|tennisclash):\/\/\S+$/i;

const linkApribileOf = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return SCHEMI_LINK_TENNIS_CLASH.test(s) ? s : null;
};

/**
 * La partita Tennis Clash dell'incontro, o `null` se il gioco non e' Tennis Clash.
 *
 * Chiavi misurate su `app-game-tennis-clash` 0.1.1 (`backend/provider.js:1009-1024`, alla creazione della
 * partita esterna): `providerData.deepLinkAndroid`, `deepLinkIos`, `matchCode` — piu' `matchConfig` e
 * `windowEndsAt`, che qui NON si leggono. Arrivano in `externalData.opened.providerData`, e solo sulla
 * lettura singola dell'incontro (`policy.js:653-655`).
 *
 * ⛔ **Il client NON costruisce mai il link** (ADR-0157 §7, `proposed`): ne' dal `matchCode` ne' da
 * `matchConfig`. Il link e' la partita, e lo compone il provider con basi che sono sue
 * (configurazione dell'organizzazione). Un campo assente resta `null`.
 * 🔴 `windowEndsAt` NON e' la validita' del link: e' la finestra del risultato (v. `crInviteOf`, la
 * trappola di #367). Non si legge, e la schermata non dice «gioca entro».
 * ⚠️ Un `providerData` che non e' un oggetto (stringa, lista, `null`) da' una partita con tutti i campi
 * `null`, non `null`: il gioco e' Tennis Clash e il dato non c'e', e la scheda lo dice.
 */
export function tennisClashPartitaOf(input: {
  externalData: unknown;
  profileType: string | null | undefined;
}): LimuloTennisClashPartita | null {
  if (input.profileType !== LIMULO_TENNIS_CLASH_PROFILE_TYPE) return null;
  const providerData = asRecord(asRecord(asRecord(input.externalData).opened).providerData);
  const code = typeof providerData.matchCode === 'string' ? providerData.matchCode.trim() : '';
  return {
    deepLinkAndroid: linkApribileOf(providerData.deepLinkAndroid),
    deepLinkIos: linkApribileOf(providerData.deepLinkIos),
    matchCode: code === '' ? null : code,
  };
}

/**
 * Quale dei due link vale su questa piattaforma di Telegram (`tgWebAppPlatform`): `ios` → iOS,
 * `android` e `android_x` → Android. **Qualunque altra** (desktop `tdesktop`/`macos`/`weba`/`webk`…,
 * o una piattaforma che non conosciamo, o nessuna) → `null`: da li' la partita non si apre, e non si
 * ripiega su un link «a caso».
 */
export function piattaformaTennisClashOf(platform: string | null | undefined): 'ios' | 'android' | null {
  if (platform === 'ios') return 'ios';
  if (platform === 'android' || platform === 'android_x') return 'android';
  return null;
}

/**
 * L'invito CR dell'incontro, o `null`.
 *
 * Chiavi misurate su `app-game-clash-royale/backend/provider.js` @ `qlash-limulo@origin/staging`
 * (2026-09-23): `providerData.remoteMatchTag`, `remoteMatchState`, `windowEndsAt`. Il tag c'e'
 * solo quando la partita e' stata creata DALL'API (endpoint partner acceso); senza, il provider
 * scrive `remoteMatchTag: ''` e qui esce `null`.
 *
 * ⛔ Nessun deep link si costruisce dal tag: il provider non ne da' uno, e un indirizzo
 * inventato da un identificativo e' esattamente cio' che ADR-0157 §7 vieta per i provider esterni.
 * ⛔ Solo per `clash-royale`: la stessa chiave in un altro provider non vuol dire la stessa cosa.
 *
 * 🔴 `windowEndsAt` NON e' la scadenza dell'invito, e qui non si legge piu'. E' la finestra del
 *    RISULTATO di Limulo (`resultWindowMinutes || timeoutMinutes || 60`, minimo 5 —
 *    `provider.js:985-986`, limulo-core-orc 2026-09-23 22:05Z): serve a cercare la partita nel
 *    battlelog. L'invito Supercell muore in ~5 minuti [M due volte il 23/09: 19:22:03→19:27:28Z e
 *    21:47:56→21:53:53Z]. #360 la mostrava come «Da giocare entro …»: con i default diceva 60
 *    minuti su un invito che ne ha 5, cioe' esattamente il modo in cui quella sera si e' perso un
 *    invito. La scadenza vera non la espone nessun campo che arrivi fin qui ⇒ la schermata dice
 *    «subito» e non inventa un'ora.
 */
export function crInviteOf(input: {
  externalData: unknown;
  profileType: string | null | undefined;
  /** `match.externalMatch`: la fonte PRINCIPALE della scadenza (v. `LimuloCrInvite.expired`). */
  externalMatch?: { status: string } | null;
}): LimuloCrInvite | null {
  if (input.profileType !== 'clash-royale') return null;
  const providerData = asRecord(asRecord(asRecord(input.externalData).opened).providerData);
  const tag = typeof providerData.remoteMatchTag === 'string' ? providerData.remoteMatchTag.trim() : '';
  if (tag === '') return null;
  const state =
    typeof providerData.remoteMatchState === 'string' && providerData.remoteMatchState.trim() !== ''
      ? providerData.remoteMatchState.trim()
      : null;
  return { tag, state, expired: externalMatchDeadOf(input.externalMatch) || state === 'timedOut' };
}
